import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import OnboardingPage from "./pages/OnboardingPage";
import Onboarding from "./pages/Onboarding";
import VerifyEmail from "./pages/VerifyEmail";
import CheckoutPage from "./pages/CheckoutPage";
import DashboardPage from "./pages/DashboardPage";
import BoardPage from "./pages/BoardPage";
import CalendarPage from "./pages/CalendarPage";
import TasksPage from "./pages/TasksPage";
import ArchivePage from "./pages/ArchivePage";
import ContactsPage from "./pages/ContactsPage";
import SettingsPage from "./pages/SettingsPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import { useEffect } from "react";
import "./App.css";

// Register Service Worker
const registerSW = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('JaySmart PWA: Service Worker registriert', registration.scope);
        })
        .catch((error) => {
          console.log('JaySmart PWA: Service Worker Fehler', error);
        });
    });
  }
};

// Flow: Signup -> Verify Email -> Checkout (Payment) -> Setup (Onboarding) -> Dashboard
const ProtectedRoute = ({ children, requireOnboarding = true, requirePayment = true }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect to checkout if not paid
  if (requirePayment && user.subscription_status !== 'active') {
    return <Navigate to="/checkout" replace />;
  }
  
  // Redirect to onboarding if not completed
  if (requireOnboarding && !user.onboarding_completed) {
    return <Navigate to="/setup" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Allow access to signup page even if logged in (user might want to see it)
  // The signup page itself will handle showing appropriate message
  if (user) {
    // Only redirect if going to login page
    const isLoginPage = window.location.pathname === '/login';
    if (isLoginPage) {
      if (user.subscription_status !== 'active') {
        return <Navigate to="/checkout" replace />;
      }
      if (!user.onboarding_completed) {
        return <Navigate to="/setup" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/checkout" element={<ProtectedRoute requirePayment={false} requireOnboarding={false}><CheckoutPage /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute requireOnboarding={false}><Onboarding /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/board" element={<ProtectedRoute><BoardPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/archive" element={<ProtectedRoute><ArchivePage /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    registerSW();
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Toaster 
            position="top-right" 
            richColors 
            theme="dark"
            toastOptions={{
              style: {
                fontFamily: 'Inter, sans-serif',
              },
            }}
          />
          <AppRoutes />
          <PWAInstallPrompt />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
